"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { NavLink, useLocation } from "react-router-dom"
import { ChevronDown, ChevronUp, X } from "lucide-react"
import { ScrollArea } from "@/shared/components/ui/scroll-area"

interface MobileNavigationProps {
  isOpen: boolean
  onClose: () => void
}

const MobileNavigation: React.FC<MobileNavigationProps> = ({ isOpen, onClose }) => {
  const [isDestinationsOpen, setIsDestinationsOpen] = useState(false)
  const [isAboutOpen, setIsAboutOpen] = useState(false)
  const { pathname } = useLocation()
  const isServicesActive = pathname.startsWith("/destination") || pathname.startsWith("/visa")
  const isAboutActive = pathname.startsWith("/about") || pathname.startsWith("/fly-and-share")

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      document.body.style.touchAction = 'none'
    } else {
      document.body.style.overflow = ''
      document.body.style.touchAction = ''
    }
    return () => {
      document.body.style.overflow = ''
      document.body.style.touchAction = ''
    }
  }, [isOpen])

  const toggleDestinations = () => {
    setIsDestinationsOpen(!isDestinationsOpen)
  }

  const toggleAbout = () => {
    setIsAboutOpen(!isAboutOpen)
  }

  return (
    <>
      {/* Backdrop overlay */}
      <div 
        className={`fixed inset-0 z-30 bg-black/50 backdrop-blur-sm transition-opacity duration-300 md:hidden ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
        aria-hidden="true"
      />
      
      <div
        className={`fixed inset-y-0 right-0 z-40 w-full sm:w-2/3 md:w-1/2 max-w-[85vw] sm:max-w-none min-h-screen transform bg-white text-black transition-smooth ease-spring mobile-nav-slide dark:bg-gray-900 ${isOpen ? "translate-x-0" : "translate-x-full"}`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation menu"
      >
      <div className="flex h-full flex-col">
        <div className="flex justify-end border-b p-4 dark:border-gray-800">
          <button
            onClick={onClose}
            aria-label="Close mobile menu"
            className="rounded-full p-2 transition-smooth hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X className="h-6 w-6 dark:text-gray-200" />
          </button>
        </div>

        <ScrollArea className="w-full flex-1 bg-white">
          <nav className="flex flex-col px-6 py-4">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `py-4 px-2 text-xl font-medium transition-smooth nav-item ${isActive ? "text-amber-600" : ""} touch-manipulation min-h-[48px] flex items-center`
              }
              onClick={onClose}
            >
              Home
            </NavLink>

            <div className="border-t border-b py-4 dark:border-gray-800">
              <button
                onClick={toggleDestinations}
                className={`flex w-full items-center justify-between text-left text-xl font-medium transition-smooth nav-item ${
                  isServicesActive ? "text-orange-500" : ""
                }`}
              >
                <span>Services</span>
                <span className="rounded-full bg-blue-200 p-1 micro-bounce">
                  {isDestinationsOpen ? (
                    <ChevronUp className="h-5 w-5 text-blue-400 transition-smooth" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-blue-500 transition-smooth" />
                  )}
                </span>
              </button>

              <div className={`mt-4 space-y-0 pl-4 mobile-submenu ${isDestinationsOpen ? "block" : "hidden"}`}>
                <NavLink
                  to="/destination"
                  className={({ isActive }) => `block transition-smooth nav-item ${isActive ? "text-orange-500" : ""}`}
                  onClick={onClose}
                >
                  <p className="text-lg font-bold">International & Domestic</p>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Explore international, domestic, and cruise travel experiences
                  </p>
                </NavLink>

                <NavLink
                  to="/visa"
                  className={({ isActive }) => `block transition-smooth nav-item ${isActive ? "text-orange-500" : ""}`}
                  onClick={onClose}
                >
                  <p className="text-lg font-bold">Visa</p>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Hassle-free visa assistance for all your travel needs
                  </p>
                </NavLink>
              </div>
            </div>

            <div className="border-t border-b py-4 dark:border-gray-800">
              <button
                onClick={toggleAbout}
                className={`flex w-full items-center justify-between text-left text-xl font-medium transition-smooth nav-item ${
                  isAboutActive ? "text-orange-500" : ""
                }`}
              >
                <span>About Us</span>
                <span className="rounded-full bg-blue-200 p-1 micro-bounce">
                  {isAboutOpen ? (
                    <ChevronUp className="h-5 w-5 text-blue-400 transition-smooth" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-blue-500 transition-smooth" />
                  )}
                </span>
              </button>

              <div className={`mt-4 space-y-0 pl-4 mobile-submenu ${isAboutOpen ? "block" : "hidden"}`}>
                <NavLink
                  to="/about"
                  className={({ isActive }) => `block transition-smooth nav-item ${isActive ? "text-orange-500" : ""}`}
                  onClick={onClose}
                >
                  <p className="text-lg font-bold">Who We Are?</p>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Learn more about our mission and travel expertise
                  </p>
                </NavLink>

                <NavLink
                  to="/fly-and-share"
                  className={({ isActive }) => `block transition-smooth nav-item ${isActive ? "text-orange-500" : ""}`}
                  onClick={onClose}
                >
                  <p className="text-lg font-bold">Fly & Share</p>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Empowering students with educational resources and hope
                  </p>
                </NavLink>
              </div>
            </div>

            <NavLink
              to="/contact"
              className={({ isActive }) =>
                `py-4 text-xl font-medium transition-smooth nav-item ${isActive ? "text-orange-500" : ""}`
              }
              onClick={onClose}
            >
              Contact
            </NavLink>
          </nav>
        </ScrollArea>

        <div className="border-t bg-white p-6 dark:border-gray-800">
          <NavLink
            to="/book-now"
            className="block w-full rounded-md bg-orange-500 px-4 py-3 text-center font-medium text-white transition-smooth hover:bg-orange-400 btn-primary"
            onClick={onClose}
          >
            Book Now
          </NavLink>
        </div>
      </div>
      </div>
    </>
  )
}

export default MobileNavigation
